/**
 * AtlasMemoryBookModule — the Memory Book panel (Passport "Memory" tab).
 *
 * The public, searchable archive of world lineage, event photos, replays, and
 * social milestones — the engine's long-term memory of what happened and to whom:
 *
 *   • Search the archive — a single box filters the mirrored entries by text,
 *     kind, correlation ref, or author.
 *   • Add an entry (`addMemoryEntry`) — choose a kind (event photo, replay,
 *     milestone, lineage, group history), write up to 280 characters, and
 *     optionally attach a correlation ref and a media key. The server stamps the
 *     author, world, and time; the client only mirrors the result.
 *   • Archive a world as a ghost (`archiveEphemeralWorld`) — an ephemeral world
 *     you own is preserved as a READ-ONLY ghost archive that lingers for 90 days
 *     (GHOST_WORLD_TTL). Memorials for large gatherings are PERMANENT.
 *
 * Memory Book entries are never edited or deleted from the client — there is no
 * such reducer. Permanence is server-owned: a row with `expiresAt === 0` is
 * permanent (memorials and milestones), everything else carries its own expiry.
 * Event-driven: re-renders on `atlas:memory-updated`.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { MemoryEntry } from '@shared/types/events.js';
import { MEMORIAL_MIN_ATTENDEES } from '@shared/constants.js';

/** Short identity hex for compact labels (`abcdef12…`). */
function shortHex(hex: string): string {
  return hex.length > 10 ? `${hex.slice(0, 8)}\u2026` : hex;
}

/** Human-readable absolute time for a micros-since-epoch instant. */
function fmtWhen(micros: bigint): string {
  if (micros <= 0n) return '';
  return new Date(Number(micros / 1000n)).toLocaleString();
}

/** A whole, non-negative number from a numeric input, or null if invalid. */
function parseCount(raw: string): number | null {
  const s = raw.trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
}

/** HTML-escape a user-authored string before it touches innerHTML. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Display label for each Memory Book entry kind. */
const KIND_LABEL: Record<string, string> = {
  event_photo: 'Photo',
  replay: 'Replay',
  milestone: 'Milestone',
  lineage: 'Lineage',
  ghost_world: 'Ghost world',
  memorial: 'Memorial',
  group_history: 'Group history',
};

export class AtlasMemoryBookModule implements IModule {
  readonly name = 'AtlasMemoryBook';

  private searchIn!: HTMLInputElement;
  private kindIn!: HTMLSelectElement;
  private worldIn!: HTMLInputElement;
  private textIn!: HTMLInputElement;
  private refIn!: HTMLInputElement;
  private mediaIn!: HTMLInputElement;
  private addBtn!: HTMLButtonElement;
  private addNote!: HTMLElement;
  private archiveBtn!: HTMLButtonElement;
  private archiveNote!: HTMLElement;
  private listEl!: HTMLElement;

  private query = '';

  init(_app: App): void {
    this.searchIn = document.getElementById('mem-search') as HTMLInputElement;
    this.kindIn = document.getElementById('mem-kind') as HTMLSelectElement;
    this.worldIn = document.getElementById('mem-world') as HTMLInputElement;
    this.textIn = document.getElementById('mem-text') as HTMLInputElement;
    this.refIn = document.getElementById('mem-ref') as HTMLInputElement;
    this.mediaIn = document.getElementById('mem-media') as HTMLInputElement;
    this.addBtn = document.getElementById('mem-add-btn') as HTMLButtonElement;
    this.addNote = document.getElementById('mem-add-note') as HTMLElement;
    this.archiveBtn = document.getElementById('mem-archive-btn') as HTMLButtonElement;
    this.archiveNote = document.getElementById('mem-archive-note') as HTMLElement;
    this.listEl = document.getElementById('mem-list') as HTMLElement;

    this.searchIn.addEventListener('input', () => {
      this.query = this.searchIn.value.trim().toLowerCase();
      this.renderList();
    });
    this.addBtn.addEventListener('click', () => this.add());
    this.archiveBtn.addEventListener('click', () => this.archive());

    // A Memory Book row changed — rebuild the (filtered) archive.
    bus.on('atlas:memory-updated', () => this.renderList());

    this.renderList();
  }

  // ─── Add ────────────────────────────────────────────────────────────────────────

  private add(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const text = this.textIn.value.trim();
    if (text.length === 0) {
      this.setNote(this.addNote, 'Write what happened before adding it.', 'err');
      return;
    }
    const world = parseCount(this.worldIn.value);
    if (world === null) {
      this.setNote(this.addNote, 'World id must be a whole number (0 = not world-scoped).', 'err');
      return;
    }
    const kind = this.kindIn.value;
    const ref = this.refIn.value.trim();
    const mediaRef = this.mediaIn.value.trim();

    this.setNote(this.addNote, 'Adding to the Memory Book\u2026', '');
    this.addBtn.disabled = true;
    void conn.reducers
      .addMemoryEntry({ kind, ref, text, mediaRef, worldId: BigInt(world) })
      .then(() => {
        this.setNote(this.addNote, 'Added \u2014 it is now part of the world\u2019s lineage.', 'ok');
        this.textIn.value = '';
        this.refIn.value = '';
        this.mediaIn.value = '';
      })
      .catch((err: Error) => this.setNote(this.addNote, err.message, 'err'))
      .finally(() => {
        this.addBtn.disabled = false;
      });
  }

  // ─── Archive ──────────────────────────────────────────────────────────────────

  private archive(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const worldId = atlas.currentWorldId;
    if (worldId === null) {
      this.setNote(this.archiveNote, 'Enter the world you want to archive first.', 'err');
      return;
    }
    this.setNote(this.archiveNote, 'Archiving this world as a ghost\u2026', '');
    this.archiveBtn.disabled = true;
    void conn.reducers
      .archiveEphemeralWorld({ worldId })
      .then(() => this.setNote(this.archiveNote, 'Archived \u2014 a read-only ghost will linger for 90 days.', 'ok'))
      .catch((err: Error) => this.setNote(this.archiveNote, err.message, 'err'))
      .finally(() => {
        this.archiveBtn.disabled = false;
      });
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  private renderList(): void {
    this.listEl.innerHTML = '';

    // A standing caption — large gatherings earn a PERMANENT Commons memorial.
    const caption = document.createElement('div');
    caption.className = 'sub';
    caption.textContent = `Events with ${MEMORIAL_MIN_ATTENDEES}+ lifetime attendees earn a permanent Commons memorial.`;
    this.listEl.appendChild(caption);

    const entries = [...atlas.memoryEntries.values()]
      .filter((e) => this.matches(e))
      .sort((a, b) => (a.at > b.at ? -1 : a.at < b.at ? 1 : 0)); // newest first

    if (entries.length === 0) {
      this.listEl.appendChild(this.empty(this.query ? 'No entries match your search.' : 'The Memory Book is empty.'));
      return;
    }
    for (const e of entries) this.listEl.appendChild(this.entryCard(e));
  }

  /** Free-text filter over text, kind, correlation ref, and author. */
  private matches(e: MemoryEntry): boolean {
    if (this.query.length === 0) return true;
    const hay = `${e.text} ${e.kind} ${KIND_LABEL[e.kind] ?? ''} ${e.ref} ${e.author}`.toLowerCase();
    return hay.includes(this.query);
  }

  private entryCard(e: MemoryEntry): HTMLElement {
    const el = document.createElement('div');
    const permanent = e.permanent || e.expiresAt === 0n;
    const ghost = e.kind === 'ghost_world';
    el.className = 'mem-item' + (permanent ? ' permanent' : '') + (ghost ? ' ghost' : '');
    el.dataset.entryId = e.entryId.toString();

    const kindLabel = KIND_LABEL[e.kind] ?? e.kind;
    const head = document.createElement('div');
    head.className = 'head';
    head.innerHTML = `<span class="kind">${esc(kindLabel)}</span><span class="when">${esc(fmtWhen(e.at))}</span>`;
    el.appendChild(head);

    const text = document.createElement('div');
    text.className = 'text';
    text.textContent = e.text;
    el.appendChild(text);

    const by = document.createElement('div');
    by.className = 'by';
    const worldBit = e.worldId !== 0n ? ` \u00b7 world #${e.worldId.toString()}` : '';
    by.textContent = `by ${shortHex(e.author)}${worldBit}`;
    el.appendChild(by);

    const life = document.createElement('div');
    life.className = 'life';
    if (permanent) {
      life.textContent = e.kind === 'memorial' ? '\u2726 Permanent memorial' : '\u2726 Permanent';
    } else {
      life.textContent = `Expires ${fmtWhen(e.expiresAt)}`;
    }
    el.appendChild(life);

    return el;
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────────

  private empty(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'empty';
    el.textContent = text;
    return el;
  }

  private setNote(node: HTMLElement, text: string, cls: '' | 'ok' | 'err'): void {
    node.className = cls ? `note ${cls}` : 'note';
    node.textContent = text;
  }
}
